// api/services/flowService.js
import api from '../client';

export const flowService = {
  // Get flow for a project
  getFlow: async (projectId) => {
    if (!projectId) {
      throw new Error('Project ID is required');
    }
    
    try {
      const { data } = await api.get(`/projects/${projectId}/flow/`);
      return data;
    } catch (error) {
      console.error(`Error fetching flow for project ${projectId}:`, error);
      throw error;
    }
  },
  
  // Save flow for a project
  saveFlow: async (projectId, flowData) => {
    if (!projectId) {
      throw new Error('Project ID is required');
    }

    try {
      // Check if we have any file uploads (DOCX or Excel templates)
      const hasFileUploads = flowData.nodes?.some(node =>
        node.type === 'output_node' && (
          node.data?.docx_config?.template instanceof File ||
          node.data?.excel_config?.template instanceof File
        )
      );

      if (hasFileUploads) {
        // Use FormData for file uploads
        const formData = new FormData();

        // Clone flowData to modify it (preserve File objects)
        const flowDataCopy = {
          ...flowData,
          nodes: [...flowData.nodes],
          edges: [...flowData.edges]
        };

        // Process nodes to handle file uploads
        flowDataCopy.nodes = flowDataCopy.nodes.map((node, index) => {
          // Clone the node to avoid mutation
          const nodeCopy = {
            ...node,
            data: { ...node.data }
          };

          // Handle DOCX template files
          if (node.type === 'output_node' &&
              node.data?.docx_config?.template instanceof File) {

            // Add file to FormData
            formData.append(`docx_template_${index}`, node.data.docx_config.template);

            // Replace file object with a reference in the copy
            nodeCopy.data = {
              ...nodeCopy.data,
              docx_config: {
                ...node.data.docx_config,
                template: `UPLOAD_REF:docx_template_${index}` // Backend will replace this
              }
            };
          }

          // Handle Excel template files
          if (node.type === 'output_node' &&
              node.data?.excel_config?.template instanceof File) {

            // Add file to FormData
            formData.append(`excel_template_${index}`, node.data.excel_config.template);

            // Replace file object with a reference in the copy
            nodeCopy.data = {
              ...nodeCopy.data,
              excel_config: {
                ...node.data.excel_config,
                template: `UPLOAD_REF:excel_template_${index}` // Backend will replace this
              }
            };
          }

          return nodeCopy;
        });

        // Add the modified flow data as JSON
        formData.append('flow_data', JSON.stringify(flowDataCopy));

        const { data } = await api.post(`/projects/${projectId}/flow/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return data;
      } else {
        // No file uploads, use regular JSON
        const { data } = await api.post(`/projects/${projectId}/flow/`, flowData);
        return data;
      }
    } catch (error) {
      console.error(`Error saving flow for project ${projectId}:`, error);
      throw error;
    }
  },

  // Start a test run with SSE streaming
  startTestRun: (flowId, transcripts, jobName, state = {}, callbacks = {}, stateHistory = [], photos = []) => {
    const { onNodeStart, onNodeComplete, onNodeError, onDone, onError } = callbacks;
    const abortController = new AbortController();

    const token = localStorage.getItem('access');

    const run = async () => {
      try {
        const response = await fetch('/client-api/test_run', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            flow_id: flowId,
            transcripts,
            job_name: jobName,
            state,
            state_history: stateHistory,
            photos,
          }),
          signal: abortController.signal,
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Test run request failed: ${response.status} ${text}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop(); // keep incomplete line in buffer

          let currentEvent = null;
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ') && currentEvent) {
              try {
                const payload = JSON.parse(line.slice(6));
                switch (currentEvent) {
                  case 'node_start':
                    onNodeStart?.(payload);
                    break;
                  case 'node_complete':
                    onNodeComplete?.(payload);
                    break;
                  case 'node_error':
                    onNodeError?.(payload);
                    break;
                  case 'done':
                    onDone?.(payload);
                    break;
                  case 'error':
                    onError?.(payload);
                    break;
                  default:
                    break;
                }
              } catch (parseErr) {
                console.error('Failed to parse SSE data:', parseErr);
              }
              currentEvent = null;
            } else if (line === '') {
              currentEvent = null;
            }
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Test run stream error:', err);
        onError?.({ message: err.message, type: 'StreamError' });
      }
    };

    run();
    return abortController;
  },
};

export default flowService;